import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, ChevronRight, Menu, X } from 'lucide-react';
import { SITE, OG_IMAGE, PRIMARY_CTA, DEMO_CTA } from './data';

const abs = (path) => (path?.startsWith('http') ? path : SITE + path);

// ---------------- Full-bleed layout primitives (shared marketing shell) ----------------
export function PageContainer({ children, className = '' }) {
  return <div className={`max-w-7xl mx-auto px-5 md:px-8 ${className}`}>{children}</div>;
}

// Full-bleed page header band. Title text must be passed verbatim (SEO H1s unchanged).
export function PageHero({ eyebrow, title, children, bg = '#EEF2F8', size = 'lg', top }) {
  const titleClass = size === 'xl'
    ? 'text-5xl md:text-6xl lg:text-7xl'
    : 'text-4xl md:text-5xl lg:text-6xl';
  return (
    <section style={{ background: bg }}>
      <PageContainer className="py-16 md:py-24">
        {top && <div className="mb-4">{top}</div>}
        {eyebrow && <div className="text-[15px] font-semibold mb-3" style={{ color: '#5B6678' }}>{eyebrow}</div>}
        <h1 className={titleClass} style={{ color: '#13294B', fontWeight: 800, letterSpacing: '-0.02em' }}>{title}</h1>
        {children && <div className="mt-5 text-[17px] md:text-[18px] leading-relaxed max-w-3xl" style={{ color: '#5B6678' }}>{children}</div>}
      </PageContainer>
    </section>
  );
}

// Full-bleed content band with an optional background color.
export function Band({ children, bg, className = '', inner = '' }) {
  return (
    <section style={bg ? { background: bg } : undefined} className={className}>
      <PageContainer className={`py-14 md:py-20 ${inner}`}>{children}</PageContainer>
    </section>
  );
}


export function Seo({ title, description, path, image = OG_IMAGE, type = 'website', jsonLd = [] }) {
  const url = abs(path || '/');
  const blocks = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={abs(image)} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={abs(image)} />
      {blocks.filter(Boolean).map((b, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(b)}</script>
      ))}
    </Helmet>
  );
}

export const breadcrumbLd = (items) => ({
  '@context': 'https://schema.org', '@type': 'BreadcrumbList',
  itemListElement: items.map((it, i) => ({ '@type': 'ListItem', position: i + 1, name: it.label, item: abs(it.href) })),
});

export function Breadcrumbs({ items }) {
  return (
    <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-1 text-xs text-slate-500 mb-4" data-testid="breadcrumbs">
      {items.map((it, i) => (
        <span key={it.href} className="flex items-center gap-1">
          {i > 0 && <ChevronRight className="w-3 h-3 text-slate-300" />}
          {i < items.length - 1 ? <Link to={it.href} className="hover:text-blue-600">{it.label}</Link> : <span className="text-slate-700 font-medium">{it.label}</span>}
        </span>
      ))}
    </nav>
  );
}

export function MarketingShell({ children }) {
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);
  const nav = [
    { label: 'Builders', href: '/construction-draw-software-for-builders' },
    { label: 'Lenders', href: '/construction-draw-software-for-lenders' },
    { label: 'Pricing', href: '/pricing' },
    { label: 'Resources', href: '/resources' },
  ];
  return (
    <div className="dr-marketing min-h-screen bg-white flex flex-col">
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b" style={{ borderColor: '#D8DEE8' }}>
        <div className="max-w-7xl mx-auto px-5 md:px-8 h-16 flex items-center gap-4">
          <Link to="/" className="flex items-center gap-2" data-testid="mk-logo">
            <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{ background: '#1E4ED8' }}>
              <span className="text-white font-bold text-xl">D</span>
            </div>
            <span className="text-2xl font-bold" style={{ color: '#13294B' }}>DrawReady</span>
          </Link>
          <nav className="hidden md:flex items-center gap-7 ml-6">
            {nav.map((n) => <Link key={n.href} to={n.href} className="text-[16px] font-medium hover:opacity-70" style={{ color: '#5B6678' }} data-testid={`nav-${n.label.toLowerCase()}`}>{n.label}</Link>)}
          </nav>
          <div className="ml-auto hidden md:flex items-center gap-4">
            <button onClick={() => navigate('/login')} className="text-[16px] font-medium hover:opacity-70" style={{ color: '#5B6678' }} data-testid="nav-login">Log in</button>
            <button onClick={() => navigate(PRIMARY_CTA.href)} className="btn-primary text-[16px]" style={{ padding: '9px 16px' }} data-testid="nav-start-trial">Start free trial</button>
          </div>
          <button className="ml-auto md:hidden" onClick={() => setOpen((v) => !v)} aria-label="Menu">{open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}</button>
        </div>
        {open && (
          <div className="md:hidden border-t px-4 py-3 space-y-2" style={{ borderColor: '#D8DEE8' }}>
            {nav.map((n) => <Link key={n.href} to={n.href} className="block text-[15px] font-medium py-1" style={{ color: '#1D2939' }} onClick={() => setOpen(false)}>{n.label}</Link>)}
            <button onClick={() => navigate('/login')} className="block text-[15px] font-medium py-1" style={{ color: '#1D2939' }}>Log in</button>
            <button onClick={() => navigate(PRIMARY_CTA.href)} className="btn-primary w-full mt-2">Start free trial</button>
          </div>
        )}
      </header>
      <main className="flex-1">{children}</main>
      <footer style={{ background: '#0E1F3A' }}>
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-14 grid grid-cols-2 md:grid-cols-4 gap-8 text-[15px]">
          <div><div className="font-bold mb-3" style={{ color: '#fff' }}>Product</div>
            <Link to="/construction-draw-software" className="block py-1 hover:opacity-70" style={{ color: '#AEB9CC' }}>Construction Draw Software</Link>
            <Link to="/construction-loan-draw-software" className="block py-1 hover:opacity-70" style={{ color: '#AEB9CC' }}>Construction Loan Draw Software</Link>
            <Link to="/construction-budget-tracking-software" className="block py-1 hover:opacity-70" style={{ color: '#AEB9CC' }}>Budget Tracking Software</Link>
            <Link to="/lien-waiver-tracking-software" className="block py-1 hover:opacity-70" style={{ color: '#AEB9CC' }}>Lien Waiver Tracking</Link>
          </div>
          <div><div className="font-bold mb-3" style={{ color: '#fff' }}>Resources</div>
            <Link to="/resources/construction-draws" className="block py-1 hover:opacity-70" style={{ color: '#AEB9CC' }}>Construction Draws</Link>
            <Link to="/resources/construction-loans" className="block py-1 hover:opacity-70" style={{ color: '#AEB9CC' }}>Construction Loans</Link>
            <Link to="/resources/lien-waivers" className="block py-1 hover:opacity-70" style={{ color: '#AEB9CC' }}>Lien Waivers</Link>
            <Link to="/resources/construction-budgeting" className="block py-1 hover:opacity-70" style={{ color: '#AEB9CC' }}>Construction Budgeting</Link>
          </div>
          <div><div className="font-bold mb-3" style={{ color: '#fff' }}>Company</div>
            <Link to="/about" className="block py-1 hover:opacity-70" style={{ color: '#AEB9CC' }}>About</Link>
            <Link to="/pricing" className="block py-1 hover:opacity-70" style={{ color: '#AEB9CC' }}>Pricing</Link>
            <Link to="/contact" className="block py-1 hover:opacity-70" style={{ color: '#AEB9CC' }}>Contact</Link>
            <Link to="/glossary" className="block py-1 hover:opacity-70" style={{ color: '#AEB9CC' }}>Glossary</Link>
            <Link to="/tools" className="block py-1 hover:opacity-70" style={{ color: '#AEB9CC' }}>Free Tools</Link>
          </div>
          <div><div className="font-bold mb-3" style={{ color: '#fff' }}>Get started</div>
            <p className="mb-4" style={{ color: '#AEB9CC' }}>Construction draw management for builders and lenders.</p>
            <Link to={PRIMARY_CTA.href} className="btn-primary text-[15px]" style={{ padding: '9px 16px' }}>{PRIMARY_CTA.label}</Link>
          </div>
        </div>
        <div className="border-t" style={{ borderColor: 'rgba(255,255,255,0.12)' }}>
          <div className="max-w-7xl mx-auto px-5 md:px-8 py-8">
            <p className="text-xs leading-relaxed max-w-4xl" style={{ color: '#AEB9CC' }} data-testid="footer-disclaimer">
              <strong style={{ color: '#fff' }}>Disclaimer:</strong> DrawReady helps builders assemble and verify draw packages against their lender's documentation requirements. DrawReady is a software tool — it does not approve draws, disburse funds, underwrite loans, or provide legal, financial, tax, accounting, or lending advice. "Bank-ready" refers to the completeness of your draw package, not a guarantee of approval or funding. Final review, approval, and disbursement of any draw remain solely with your lender. DrawReady is not a bank, lender, or financial institution.
            </p>
            <div className="flex flex-col md:flex-row justify-between items-center gap-3 mt-6">
              <p className="text-xs" style={{ color: '#8A97AD' }}>© {new Date().getFullYear()} DrawReady, Inc. · Cedar Rapids, Iowa</p>
              <div className="flex items-center gap-5 text-xs">
                <Link to="/privacy" className="hover:opacity-70" style={{ color: '#AEB9CC' }}>Privacy</Link>
                <Link to="/terms" className="hover:opacity-70" style={{ color: '#AEB9CC' }}>Terms</Link>
                <Link to="/disclaimer" className="hover:opacity-70" style={{ color: '#AEB9CC' }}>Disclaimer</Link>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export function CtaInline({ pain = 'Stop chasing draw documents.', relief = 'DrawReady shows you what’s missing before you submit.' }) {
  return (
    <div className="my-8 rounded-xl border p-6" style={{ borderColor: '#D8DEE8', background: '#F2F4F7' }} data-testid="cta-inline">
      <p className="text-lg font-bold" style={{ color: '#13294B' }}>{pain}</p>
      <p className="mt-1" style={{ color: '#5B6678' }}>{relief}</p>
      <Link to={PRIMARY_CTA.href} className="btn-primary mt-4 text-[15px]">{PRIMARY_CTA.label}</Link>
    </div>
  );
}

export function CtaBlock() {
  return (
    <div className="mt-12 rounded-xl p-8 md:p-10" style={{ background: '#13294B' }} data-testid="cta-block">
      <h2 className="text-2xl md:text-3xl" style={{ color: '#fff' }}>Ready to get your next draw bank-ready?</h2>
      <p className="mt-3 max-w-xl" style={{ color: '#C7D0DE' }}>Your project shouldn’t stop because your draw isn’t ready. DrawReady checks it before it reaches your lender.</p>
      <div className="flex flex-wrap items-center gap-3 mt-6">
        <Link to={PRIMARY_CTA.href} className="btn-primary on-dark">{PRIMARY_CTA.label}</Link>
        <Link to={DEMO_CTA.href} className="btn-secondary on-dark">{DEMO_CTA.label}</Link>
      </div>
    </div>
  );
}

export function RelatedList({ items }) {
  if (!items?.length) return null;
  return (
    <div className="mt-12 border-t border-slate-200 pt-8" data-testid="related-articles">
      <h2 className="text-xl font-bold text-slate-900 mb-4">Related reading</h2>
      <div className="grid sm:grid-cols-2 gap-4">
        {items.map((a) => (
          <Link key={a.slug} to={`/resources/${a.slug}`} className="rounded-xl border border-slate-200 p-4 hover:border-blue-300 hover:shadow-sm transition">
            <div className="text-[11px] font-bold uppercase tracking-wider text-blue-600">{a.category}</div>
            <div className="font-semibold text-slate-900 mt-1">{a.h1}</div>
            <div className="text-sm text-slate-500 mt-1 line-clamp-2">{a.dek}</div>
          </Link>
        ))}
      </div>
    </div>
  );
}
